/**
 * CheckDocument class
 *
 * @class CheckDocument
 * @classdesc This class is responsible for checking an uploaded document for a document type.
 *
 */
class CheckDocument {
	/**
	 * Configuration config for the module.
	 * @type {Object}
	 */
	constructor() {
		this.initConfig();
		this.initDomElements();
		this.initEvents();
	}

	/**
	 * Initializes configuration settings for the document checker
	 *
	 * @returns {void}
	 */
	initConfig = () => {
		this.pondInstance = null;
		this.config = {
			path: 'docreader-endpoint',
			pondInstance: null,
			basePath: null,
			url: null,
			timeout: 15000,
			requestInterval: 2000,
			initialDelay: 5000,
			feedbackMessages: {
				validStatusTitle: 'Arbo-/werkhervattingsrapportage',
				invalidStatusTitle: 'Document niet gevalideerd',
			},
			selectors: {
				validating: '[data-uploader-overlay-validating]',
				valid: '[data-uploader-overlay-valid]',
				invalid: '[data-uploader-overlay-invalid]',
				statusMessageA: '[data-uploader-status-message-a]',
				statusMessageB: '[data-uploader-status-message-b]',
				werknemerNaam: '[data-uploader-werknemer-naam]',
				validStatusTitle: '[data-uploader-valid-status-title]',
				validStatusMessage: '[data-uploader-valid-status-message]',
				invalidStatusTitle: '[data-uploader-invalid-status-title]',
				invalidStatusMessage: '[data-uploader-invalid-status-message]',
				removeButton: '[data-uploader-remove]',
				buttonFeedbackModal: '[data-uploader-open-feedback-modal]',
				modalMessageGeenWerkhervattingsRapportage: '[data-modal-message-geen-werkhervattingsrapportage]',
				modalMessageDocumentNietRecent: '[data-modal-message-document-niet-recent]',
				modalRemoveUpload: '[data-modal-remove-upload]',
				formSubmitButton: '[data-form-actie] button[type="submit"]',
			},
		};
		this.config.basePath = window.app.ajaxPath;
		this.config.url = this.formatAjaxUrl(this.config.basePath, this.config.path);
	};

	/**
	 * Initializes DOM element references
	 *
	 * @returns {void}
	 */
	initDomElements = () => {
		this.validatingElement = document.querySelector(this.config.selectors.validating);
		this.validElement = document.querySelector(this.config.selectors.valid);
		this.invalidElement = document.querySelector(this.config.selectors.invalid);
		this.werknemerNaamElement = document.querySelector(this.config.selectors.werknemerNaam);
		this.validStatusTitleElement = document.querySelector(this.config.selectors.validStatusTitle);
		this.validStatusMessageElement = document.querySelector(this.config.selectors.validStatusMessage);
		this.invalidStatusTitleElement = document.querySelector(this.config.selectors.invalidStatusTitle);
		this.invalidStatusMessageElement = document.querySelector(this.config.selectors.invalidStatusMessage);
		this.removeButtonElement = document.querySelector(this.config.selectors.removeButton);
		this.buttonFeedbackModalElement = document.querySelector(this.config.selectors.buttonFeedbackModal);
		this.modalMessageGeenWerkhervattingsRapportageElement = document.querySelector(this.config.selectors.modalMessageGeenWerkhervattingsRapportage);
		this.modalMessageDocumentNietRecentElement = document.querySelector(this.config.selectors.modalMessageDocumentNietRecent);
		this.formSubmitButtonElement = document.querySelector(this.config.selectors.formSubmitButton);
	};

	/**
	 * Sets up event listeners for FilePond and button interactions
	 *
	 * @returns {void}
	 */
	initEvents = () => {
		// Init FilePond file processing when filepond is initialized
		document.body.addEventListener('FilePond:init', () => {
			this.initFileProcessedEventHandler();
			this.initDomElements();
		});

		// Remove file from FilePond when remove button is clicked
		document.body.addEventListener('click', (event) => {
			if (event.target.closest(this.config.selectors.removeButton)) {
				console.log('removeButton clicked');
				this.removeFileHandler();
			}
		});
		// Remove file from FilePond when remove button is clicked
		document.body.addEventListener('click', (event) => {
			if (event.target.closest(this.config.selectors.modalRemoveUpload)) {
				console.log('removeButton clicked');
				this.removeFileHandler();
			}
		});
	};

	/**
	 * Initializes FilePond file processing event handlers
	 *
	 * @returns {void}
	 */
	initFileProcessedEventHandler = () => {
		window.pond.forEach((pond) => {
			pond.on('processfile', (error, file) => {
				if (error) return;
				console.log('File processed:', file.serverId);
				const serverId = this.parseServerId(file.serverId);
				this.pondInstance = pond;
				if (typeof serverId !== 'undefined') {
					this.initCheckResult(serverId, pond);
				} else {
					console.error('No server id returned from FilePond...');
					this.fileInputElementSetLoadingState(false);
					this.enableFormSubmitButton();
				}
			});

			pond.on('addfile', (error) => {
				if (error) return;
				this.disableFormSubmitButton();
			});
		});
	};

	/**
	 * Disables the form submit button
	 *
	 * @returns {void}
	 */
	disableFormSubmitButton = () => {
		this.formSubmitButtonElement.setAttribute('disabled', 'disabled');
	};

	/**
	 * Enables the form submit button
	 *
	 * @returns {void}
	 */
	enableFormSubmitButton = () => {
		this.formSubmitButtonElement.removeAttribute('disabled');
	};

	/**
	 * Handles file removal from FilePond instance
	 *
	 * @returns {void}
	 */
	removeFileHandler = () => {
		const files = this.pondInstance.getFiles();
		if (files && files.length > 0) {
			files.forEach((file) => {
				this.pondInstance.removeFile(file.id, { revert: true });
			});
		}
		this.fileInputElementResetState(false);
	};

	/**
	 * Parses the server ID from FilePond response
	 *
	 * @param {string} serverId - The server response ID containing file information
	 *
	 * @returns {string} The parsed server ID
	 */
	parseServerId = (serverId) => {
		const exploded = serverId.split('|');
		return exploded[1];
	};

	/**
	 * Initializes the document check process
	 *
	 * @param {string} serverId - The server ID of the uploaded file
	 * @param {Object} pondElement - The FilePond instance
	 *
	 * @returns {void}
	 */
	initCheckResult = (serverId, pondElement) => {
		this.fileInputElementSetLoadingState(pondElement, true);
		setTimeout(() => {
			this.checkResult(serverId, pondElement);
			console.info('checkResult: Send initial request after ' + this.config.initialDelay + 'ms');
		}, this.config.initialDelay);
	};

	/**
	 * Formats the AJAX URL by combining the base path and path
	 *
	 * @param {string} basePath - The base path of the URL
	 * @param {string} path - The specific path to be appended
	 *
	 * @returns {string} The formatted AJAX URL
	 */
	formatAjaxUrl = (basePath, path) => {
		const url = basePath + path;
		return url;
	};

	/**
	 * Checks the result by making an AJAX request
	 *
	 * @param {string} serverId - The ID of the uploaded file on the server
	 * @param {Object} pondElement - The FilePond instance
	 *
	 * @returns {void}
	 */
	checkResult = (serverId, pondElement) => {
		this.disableFormSubmitButton();
		const startTime = Date.now();
		let resultTimer = 0;
		const checkTimeout = () => {
			resultTimer++;
			if (Date.now() - startTime > this.config.timeout) {
				console.error('Error: Request timed out');
				this.fileInputElementSetLoadingState(false);
				this.enableFormSubmitButton();
				return;
			}
			fetch(this.config.url + '/' + serverId)
				.then((response) => response.text())
				.then((data) => {
					const parsedData = JSON.parse(data);
					console.info(parsedData);
					try {
						const parsedData = JSON.parse(data);
						if (parsedData.status == 'inProgress') {
							console.info('Send request: No result yet...');
							setTimeout(checkTimeout, this.config.requestInterval);
						} else if (parsedData.status == 'success') {
							console.info('Send request: Success...');
							console.info('Processing time:', this.formatResultTime(resultTimer));
							this.enableFormSubmitButton();
							this.processResult(parsedData.result.data, pondElement);
						} else if (parsedData.status == 'failed') {
							console.warn('Send request: Failed...');
							this.fileInputElementSetLoadingState(false);
							this.enableFormSubmitButton();
						}
					} catch (error) {
						console.error('Error parsing JSON:', error);
					}
				})
				.catch((error) => {
					console.error('Error:', error);
				});
		};
		console.log('Checking result...');
		checkTimeout();
	};

	/**
	 * Opens the feedback modal with appropriate messages
	 *
	 * @param {Object} result - The validation result object
	 * @param {boolean} result.arbo_or_werkhervattingsrapportage - Indicates if document is a valid report
	 * @param {string} result.datum_onderzoek - The examination date from the document
	 *
	 * @returns {void}
	 */
	openFeedbackModal = (result) => {
		this.modalMessageGeenWerkhervattingsRapportageElement.classList.add('d-none');
		this.modalMessageDocumentNietRecentElement.classList.add('d-none');
		if (result.arbo_or_werkhervattingsrapportage === false) {
			this.modalMessageGeenWerkhervattingsRapportageElement.classList.remove('d-none');
		} else if (!this.validateDatumOnderzoek(result.datum_onderzoek)) {
			this.modalMessageDocumentNietRecentElement.classList.remove('d-none');
		}
		this.buttonFeedbackModalElement.click();
	};

	/**
	 * Formats the processing time for logging
	 *
	 * @param {number} resultTimeCount - The number of check attempts
	 *
	 * @returns {string} Formatted time string with milliseconds
	 */
	formatResultTime = (resultTimeCount) => {
		return resultTimeCount * this.config.requestInterval + this.config.initialDelay + 'ms';
	};

	/**
	 * Processes the document validation result
	 *
	 * @param {Object} result - The validation result object
	 * @param {boolean} result.arbo_or_werkhervattingsrapportage - Indicates if document is a valid report
	 * @param {string} result.datum_onderzoek - The examination date from the document
	 *
	 * @returns {void}
	 */
	processResult = (result) => {
		if (result) {
			console.log(result);
			// arbo_or_werkhervattingsrapportage === true && validateDatumOnderzoek(datum_onderzoek)
			// U heeft een document geupload voor mevrouw D.E. Tester.
			// Volgens onze controle is het document bruikbaar voor de behandeling van het dossier.

			// arbo_or_werkhervattingsrapportage === false
			// Het document dat u heeft geupload is volgens onze controle geen werkhervattingsrapportage.
			// Wilt u het document toch aanleveren? Dat kan. De kans is echter groot dat het niet bruikbaar is voor de behandeling van het dossier.

			// arbo_or_werkhervattingsrapportage === true && !validateDatumOnderzoek(datum_onderzoek)
			// Echter is het document niet recent genoeg. Controleer of u een recentere versie van het document heeft.
			// Wilt u het document toch aanleveren? Dat kan. De kans is echter groot dat het niet bruikbaar is voor de behandeling van het dossier.

			if (this.validateResult(result)) {
				this.processResultSuccess(result);
			} else {
				this.processResultInvalid(result);
			}
		}
	};

	/**
	 * Validates the document check result
	 *
	 * @param {Object} result - The validation result object
	 * @param {boolean} result.arbo_or_werkhervattingsrapportage - Indicates if document is a valid report
	 * @param {string} result.datum_onderzoek - The examination date from the document
	 *
	 * @returns {boolean} True if document is valid, false otherwise
	 */
	validateResult = (result) => {
		console.log('validateResult:', result);
		if (result.arbo_or_werkhervattingsrapportage === true && this.validateDatumOnderzoek(result.datum_onderzoek)) {
			return true;
		} else if (result.arbo_or_werkhervattingsrapportage === false) {
			return false;
		} else if (result.arbo_or_werkhervattingsrapportage === true && !this.validateDatumOnderzoek(result.datum_onderzoek)) {
			return false;
		}
	};

	/**
	 * Validates if the document's examination date is within acceptable range
	 *
	 * @param {string} datumOnderzoek - The examination date to validate (YYYY-MM-DD format)
	 *
	 * @returns {boolean} True if date is within range (not older than 20 weeks), false otherwise
	 */
	validateDatumOnderzoek = (datumOnderzoek) => {
		// Check if date is not older than 8 weeks before today
		if (!datumOnderzoek) {
			return false;
		}

		const onderzoekDate = new Date(datumOnderzoek);
		const today = new Date();

		// Calculate weeks between dates
		const oneWeek = 7 * 24 * 60 * 60 * 1000; // days*hours*minutes*seconds*milliseconds
		const diffWeeks = Math.round(Math.abs((today - onderzoekDate) / oneWeek));

		return diffWeeks <= 20;
	};

	/**
	 * Processes a successful document validation
	 *
	 * @param {Object} result - The validation result object
	 * @param {string} result.werknemer_naam - The name of the employee
	 *
	 * @returns {void}
	 */
	processResultSuccess = (result) => {
		console.log('Processing result...', result);
		this.fileInputElementSetLoadingState(false);
		this.fileInputElementSetValidState(result);
	};

	/**
	 * Processes an invalid document validation
	 *
	 * @param {Object} result - The validation result object
	 * @param {boolean} result.arbo_or_werkhervattingsrapportage - Indicates if document is a valid report
	 * @param {string} result.datum_onderzoek - The examination date from the document
	 *
	 * @returns {void}
	 */
	processResultInvalid = (result) => {
		console.log('Processing result...', result);
		this.fileInputElementSetLoadingState(false);
		this.fileInputElementSetInvalidState(result);
		this.openFeedbackModal(result);
	};

	/**
	 * Sets the loading state of the file input element
	 *
	 * @param {boolean} loading - Whether the element should show loading state
	 *
	 * @returns {void}
	 */
	fileInputElementSetLoadingState = (loading) => {
		if (loading) {
			this.toggleStatusMessages();
			this.validatingElement.classList.remove('d-none');
		} else {
			this.toggleStatusMessages();
			this.validatingElement.classList.add('d-none');
		}
	};

	/**
	 * Resets the file input element state
	 *
	 * @returns {void}
	 */
	fileInputElementResetState = () => {
		this.validatingElement.classList.add('d-none');
		this.validElement.classList.add('d-none');
		this.invalidElement.classList.add('d-none');
	};

	/**
	 * Toggles visibility of status messages with animation
	 *
	 * @returns {void}
	 */
	toggleStatusMessages = () => {
		const messageA = document.querySelector(this.config.selectors.statusMessageA);
		const messageB = document.querySelector(this.config.selectors.statusMessageB);

		if (messageA.classList.contains('d-none')) {
			messageA.classList.remove('d-none');
			messageB.classList.add('d-none');
		} else {
			messageA.classList.add('d-none');
			messageB.classList.remove('d-none');
		}

		// Only set timeout if elements exist to prevent infinite loop with broken elements
		if (messageA && messageB) {
			setTimeout(() => {
				this.toggleStatusMessages();
			}, 3000);
		}
	};

	/**
	 * Sets the valid state UI for the file input element
	 *
	 * @param {Object} result - The validation result object
	 * @param {string} result.werknemer_naam - The name of the employee
	 *
	 * @returns {void}
	 */
	fileInputElementSetValidState = (result) => {
		this.validStatusTitleElement.textContent = this.config.feedbackMessages.validStatusTitle;
		console.log(result);
		this.validStatusMessageElement.textContent = result.werknemer_naam;
		this.validElement.classList.remove('d-none');
		this.invalidElement.classList.add('d-none');
	};

	/**
	 * Sets the invalid state UI for the file input element
	 *
	 * @param {Object} result - The validation result object
	 * @param {boolean} result.arbo_or_werkhervattingsrapportage - Indicates if document is a valid report
	 * @param {string} result.datum_onderzoek - The examination date from the document
	 *
	 * @returns {void}
	 */
	fileInputElementSetInvalidState = (result) => {
		this.invalidStatusTitleElement.textContent = this.config.feedbackMessages.invalidStatusTitle;
		if (result.arbo_or_werkhervattingsrapportage === false) {
			this.invalidStatusMessageElement.textContent = 'Geen Arbo-/werkhervattingsrapport';
		} else if (!this.validateDatumOnderzoek(result.datum_onderzoek)) {
			this.invalidStatusMessageElement.textContent = 'Het document is niet recent genoeg...';
		}
		this.validElement.classList.add('d-none');
		this.invalidElement.classList.remove('d-none');
	};
}

export default CheckDocument;
